import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toastification';
import { isValidEmailSyntax } from '@/utilities/validators/email';
export function useSendTestEmailDialog(params) {
    const { modelValue, campaignTitle, template, preheader, senderEmail, senderName, replyToEmail, sendFn, onSent } = params;
    const { t } = useI18n();
    const toast = useToast();
    const modelValueRef = modelValue;
    const isOpen = computed({
        get: () => modelValueRef.value,
        set: (value) => {
            modelValueRef.value = value;
        },
    });
    const destEmailsText = ref('');
    const destEmailsInputRef = ref(null);
    const sending = ref(false);
    const hasInvalidEmails = ref(false);
    const isShowingTooltip = ref(false);
    const tooltipKey = ref('invalid-email-list');
    const tooltipText = computed(() => t(`breadcrumb.email.campaign.send-email-test-dialog.errors.${tooltipKey.value}`));
    const showToast = {
        success: (message) => toast.success(message, { timeout: 3000 }),
        error: (message) => toast.error(message, { timeout: 4000 }),
    };
    watch(() => modelValueRef.value, value => {
        if (value) {
            destEmailsText.value = '';
            hasInvalidEmails.value = false;
            isShowingTooltip.value = false;
            tooltipKey.value = 'invalid-email-list';
            return;
        }
        isShowingTooltip.value = false;
    });
    const showValidationTooltip = (key) => {
        tooltipKey.value = key;
        isShowingTooltip.value = true;
    };
    const isValidEmail = (email) => isValidEmailSyntax(email);
    const parseEmails = (raw) => {
        return (raw || '')
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .split(/\n|,/g)
            .map(s => s.trim())
            .filter(Boolean);
    };
    const normalizeDestEmails = () => {
        const current = destEmailsText.value || '';
        const normalized = current
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/,[ \t]+/g, ',\n')
            .replace(/\n{2,}/g, '\n');
        if (normalized !== current)
            destEmailsText.value = normalized;
        if (isShowingTooltip.value) {
            const emails = parseEmails(destEmailsText.value);
            const hasInvalid = emails.some(email => !isValidEmail(email));
            hasInvalidEmails.value = hasInvalid;
            if (!hasInvalid)
                isShowingTooltip.value = false;
        }
    };
    const handleDestEmailsBlur = () => {
        const emails = parseEmails(destEmailsText.value);
        const hasInvalid = emails.length > 0 && emails.some(email => !isValidEmail(email));
        hasInvalidEmails.value = hasInvalid;
        isShowingTooltip.value = hasInvalid;
    };
    const handleDestEmailsKeydown = (event) => {
        if (event.key !== ',')
            return;
        event.preventDefault();
        const textarea = destEmailsInputRef.value?.textarea;
        const current = destEmailsText.value || '';
        if (!textarea) {
            if (current.length === 0) {
                destEmailsText.value = ',';
                return;
            }
            destEmailsText.value = current.endsWith('\n') ? `${current},` : `${current},\n`;
            return;
        }
        const start = textarea.selectionStart ?? current.length;
        const end = textarea.selectionEnd ?? start;
        const insert = ',\n';
        const next = `${current.slice(0, start)}${insert}${current.slice(end)}`;
        destEmailsText.value = next;
        requestAnimationFrame(() => {
            try {
                textarea.focus();
                textarea.setSelectionRange(start + insert.length, start + insert.length);
            }
            catch {
                return;
            }
        });
    };
    const handleDestEmailsPaste = (event) => {
        const clipboardText = event.clipboardData?.getData('text');
        if (!clipboardText)
            return;
        const insert = clipboardText
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/,\s*/g, ',\n')
            .replace(/\n{2,}/g, '\n');
        const textarea = destEmailsInputRef.value?.textarea;
        const current = destEmailsText.value || '';
        if (!textarea) {
            destEmailsText.value = `${current}${insert}`;
            return;
        }
        event.preventDefault();
        const start = textarea.selectionStart ?? current.length;
        const end = textarea.selectionEnd ?? start;
        destEmailsText.value = `${current.slice(0, start)}${insert}${current.slice(end)}`;
        normalizeDestEmails();
        requestAnimationFrame(() => {
            try {
                textarea.focus();
                const nextPos = start + insert.length;
                textarea.setSelectionRange(nextPos, nextPos);
            }
            catch {
                return;
            }
        });
    };
    const validateBeforeSend = () => {
        const emails = parseEmails(destEmailsText.value);
        if (emails.length === 0) {
            hasInvalidEmails.value = true;
            showValidationTooltip('required-dest-email');
            return { ok: false };
        }
        const invalidEmails = emails.filter(email => !isValidEmail(email));
        if (invalidEmails.length > 0) {
            hasInvalidEmails.value = true;
            showValidationTooltip('invalid-email-list');
            return { ok: false };
        }
        const title = (campaignTitle.value || '').trim();
        if (!title) {
            return { ok: false };
        }
        const tpl = (template.value || '').trim();
        if (!tpl) {
            return { ok: false };
        }
        hasInvalidEmails.value = false;
        return { ok: true, emails };
    };
    const handleSend = async () => {
        const validated = validateBeforeSend();
        if (!validated.ok)
            return;
        try {
            sending.value = true;
            const senderEmailValue = (senderEmail.value || '').trim();
            const senderNameValue = (senderName.value || '').trim();
            const replyToEmailValue = ((replyToEmail.value || '').trim() || senderEmailValue).trim();
            const preheaderValue = (preheader.value || '').trim();
            await sendFn({
                dest_email: validated.emails.join(','),
                title: campaignTitle.value.trim(),
                template: template.value,
                preheader: preheaderValue,
                sender_email: senderEmailValue,
                sender_name: senderNameValue,
                reply_to_email: replyToEmailValue,
            });
            showToast.success(t('breadcrumb.email.campaign.send-email-test-dialog.toast-success'));
            onSent?.();
            isOpen.value = false;
        }
        catch (error) {
            showToast.error(t('breadcrumb.email.campaign.send-email-test-dialog.toast-error'));
        }
        finally {
            sending.value = false;
        }
    };
    return {
        isOpen,
        destEmailsText,
        destEmailsInputRef,
        sending,
        hasInvalidEmails,
        isShowingTooltip,
        tooltipText,
        normalizeDestEmails,
        handleDestEmailsPaste,
        handleDestEmailsBlur,
        handleDestEmailsKeydown,
        handleSend,
    };
}
